import { getApiErrorHttpStatus, getApiErrorMessage } from '@mi-front/core';

/**
 * How a failed query should be presented to the user.
 * - `notFound`: the resource does not exist (HTTP 404).
 * - `forbidden`: the user is authenticated but not allowed to see it (HTTP 401/403).
 * - `unavailable`: the request never got a usable response (network error, timeout).
 * - `error`: anything else (validation, server errors, unexpected exceptions).
 */
export type ViewErrorKind = 'notFound' | 'forbidden' | 'unavailable' | 'error';

/**
 * Classifies an error thrown by an API client (or any unknown throwable) so the UI can
 * show a meaningful state instead of dumping the raw backend message.
 */
export function getViewErrorKind(error: unknown): ViewErrorKind {
  const status = getApiErrorHttpStatus(error);

  if (status === 404) {
    return 'notFound';
  }

  if (status === 401 || status === 403) {
    return 'forbidden';
  }

  // No status means the response never arrived (offline, DNS, CORS, aborted request).
  if (status == null && isLikelyNetworkError(error)) {
    return 'unavailable';
  }

  return 'error';
}

/**
 * Raw technical detail for support/debugging: the backend message when available,
 * falling back to the JS error message. Never used as the primary user-facing copy.
 */
export function getViewErrorDetail(error: unknown): string | undefined {
  const apiMessage = getApiErrorMessage(error);

  if (apiMessage) {
    return apiMessage;
  }

  if (error instanceof Error) {
    return error.message || error.constructor.name;
  }

  if (typeof error === 'string') {
    return error;
  }

  return undefined;
}

/** True when the error looks like a transport failure rather than an HTTP response. */
function isLikelyNetworkError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const code = (error as { code?: unknown }).code;

  return code === 'ERR_NETWORK' || code === 'ECONNABORTED' || code === 'ETIMEDOUT';
}
